/**
 * A global wrapper service for making HTTP requests with middleware support.
 *
 * @module services/request
 */
import superagent from 'superagent'
import { xClientAttributesMiddleware } from './x-client-attributes/middleware'
import { timeoutMiddleware } from './timeout/middleware'

const middlewares = [xClientAttributesMiddleware, timeoutMiddleware]

/**
 * Applies every middleware to the request.
 *
 * @param {superagent.Request} req - The SuperAgent request object.
 * @returns {superagent.Request} The request with the middlewares applied.
 */
const applyMiddlewares = (req) =>
  middlewares.reduce((currentReq, middleware) => middleware(currentReq), req)

/**
 * Global wrapper for making HTTP requests with multiple middlewares support.
 *
 * @type {Object}
 */
const request = {
  /**
   * Send a GET request to the specified URL with the middlewares applied.
   *
   * @param {string} url - The URL to send the request to.
   * @returns {superagent.Request} The SuperAgent request object.
   */
  get: (url) => applyMiddlewares(superagent.get(url)),

  /**
   * Send a POST request to the specified URL with the middlewares applied.
   *
   * @param {string} url - The URL to send the request to.
   * @returns {superagent.Request} The SuperAgent request object.
   */
  post: (url) => applyMiddlewares(superagent.post(url)),

  /**
   * Send a PUT request to the specified URL with the middlewares applied.
   *
   * @param {string} url - The URL to send the request to.
   * @returns {superagent.Request} The SuperAgent request object.
   */
  put: (url) => applyMiddlewares(superagent.put(url)),

  /**
   * Send a PATCH request to the specified URL with the middlewares applied.
   *
   * @param {string} url - The URL to send the request to.
   * @returns {superagent.Request} The SuperAgent request object.
   */
  patch: (url) => applyMiddlewares(superagent.put(url)),

  /**
   * Send a DELETE request to the specified URL with the middlewares applied.
   *
   * @param {string} url - The URL to send the request to.
   * @returns {superagent.Request} The SuperAgent request object.
   */
  delete: (url) => applyMiddlewares(superagent.delete(url)),
}

export default request
